import { ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetMetricTargetsQuery } from '../../api';
import { useAuthentication } from '../../auth';
import { Button } from '../../components/Button';
import { DialogTrigger } from '../../components/Dialog';
import { Heading } from '../../components/Heading';
import { dateRanges } from '../../components/Input';
import { DateRangePickerInput } from '../../components/Input/DateRangePickerInput';
import { Skeleton } from '../../components/Skeleton';
import { isAdmin, isSuperAdmin } from '../../util/auth';
import { isMonthDateRange } from '../../util/date';
import { ClinicsComparisonCard } from '../components/ClinicsComparisonCard';
import { EditMonthlyTargetsDialog } from '../components/EditMonthlyTargetsDialog';
import { useClinicsInsights } from '../hooks/useClinicsInsights';

export const ClinicsDashboard = () => {
  const currentUser = useAuthentication();
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(dateRanges.last30days.value);
  const { data: clinicsInsights, isLoading: isLoadingData } = useClinicsInsights({ dateRange });
  const { data: targets, isLoading: isLoadingTargets } = useGetMetricTargetsQuery({ timespan: 'month' });

  const isLoading = isLoadingData || isLoadingTargets;

  const showTargets = useMemo(() => !!dateRange && isMonthDateRange(dateRange), [dateRange]);

  return (
    <div className="flex flex-1 flex-col justify-between">
      <div className="mb-5 flex items-center justify-center md:justify-between">
        <Heading size="l" className="hidden md:block">
          Clinics Dashboard
        </Heading>

        <div className="flex flex-col items-center gap-5 md:flex-row">
          <DateRangePickerInput
            value={dateRange}
            onChange={setDateRange}
            className="md:w-[350px]"
            testId="date-range-input"
          />

          {isSuperAdmin(currentUser) && (
            <EditMonthlyTargetsDialog>
              <DialogTrigger asChild>
                <Button variant="primary" data-testid="edit-monthly-targets-button">
                  Edit Monthly Targets
                </Button>
              </DialogTrigger>
            </EditMonthlyTargetsDialog>
          )}

          {isAdmin(currentUser) && (
            <Link to="/admin/clinics">
              <Button variant="primary" icon={ExternalLink}>
                Edit Clinic Censuses
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="mb-10 flex flex-1 flex-col gap-5">
        {isLoading ? (
          <Skeleton className="h-[600px] w-full rounded-2xl" />
        ) : (
          <ClinicsComparisonCard
            data={clinicsInsights}
            targets={targets}
            showTargets={showTargets}
            onShowTargetsClick={() => {
              setDateRange(dateRanges.last30days.value);
            }}
          />
        )}
      </div>
    </div>
  );
};
